import type { ReactNode } from 'react'

// The keyboard-reachable half of a clickable table row.
//
// `<tr onClick={...}>` is a mouse-only control: a <tr> takes no focus, carries
// no role, and announces nothing, so a worker record that opens by clicking
// its row cannot be opened by keyboard at all — and a screen reader is never
// told the row does anything. Adding tabIndex to the <tr> does not fix that:
// it produces a focusable element with no role and no name.
//
// The fix is a real <button> inside the row, carrying the accessible name.
// The row keeps its own onClick as a mouse convenience, so pointer behaviour
// is unchanged; this button stops propagation so the two never both fire.
//
// The default class is the house 44px target (see BackButton). Pass
// `className` when the visible content is its own affordance — a status pill,
// say — and the 44px box would distort it.
export default function RowOpenButton({ label, onOpen, className, children }: {
  /** What a screen reader reads. Name the RECORD, not the widget: "Open
   *  worker Thabo Nkosi", never "Open" or "Edit". */
  label:      string
  onOpen:     () => void
  className?: string
  /** Visible content — usually an icon or a status chip. */
  children:   ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={e => { e.stopPropagation(); onOpen() }}
      className={className ?? 'h-11 w-11 inline-flex items-center justify-center rounded-lg text-gray-500 hover:text-white light:hover:text-fg hover:bg-surface'}>
      {children}
    </button>
  )
}
